<template>
  <div
    v-show="rowVisible"
    class="swing-tile-row"
    :data-row-index="rowIndex"
    :data-row-color="rowMarkColor || undefined"
    :row__color="rowMarkColor || undefined"
    @click.capture="onRowClick"
    @contextmenu.stop.prevent="onRowContextMenu"
  >
    <MPanel v-if="panelData" :data="panelData" :page-id="pageId" />
    <div v-else class="swing-tile-row-fallback" v-html="fallbackHtml"></div>
  </div>
</template>

<script setup lang="ts">
import { SwingTilesEmbedKey, SwingTilesRowScopeKey } from '@/components/platform/injection-keys'
import MPanel from '@/components/platform/panel/Panel.vue'
import { sanitizePlatformDisplayHtml } from '@/common/utils/html-sanitizer'
import { buildTilesRowDataChangeSignature } from '@/common/utils/tilesInlineAfterLoad'
import { getTilesRowActionVisibility } from '@/common/utils/tilesSetInlineVisibility'
import { readSwingRowString } from '@/common/utils/treeTableRowSwingPresentation'
import type { TilesRowActionVisibilityMap } from '@/common/utils/tilesSetInlineVisibility'
import { computed, nextTick, onMounted, provide, watch } from 'vue'

type TilesRowClickPayload = {
  data: unknown
  selectedIdx: number
}

type TilesRowContextMenuPayload = {
  data: unknown
  selectedIdx: number
  target: EventTarget | null
  originalEvent: MouseEvent
}

const props = defineProps<{
  rowData?: unknown
  panelData: any
  pageId: string | number
  rowIndex: number
  tableFieldName: string
  tilesElementId: number
  parentPanelId: string | number
  rowVisible: boolean
  commitRowField: (fieldName: string, value: unknown) => void
  getRowFieldValue: (fieldName: string) => unknown
  rowActionVisibilityMap?: TilesRowActionVisibilityMap
  afterRowMount?: () => void | Promise<void>
}>()

const emit = defineEmits<{
  click: [payload: TilesRowClickPayload]
  contextmenu: [payload: TilesRowContextMenuPayload]
}>()

const isInteractiveTarget = (target: EventTarget | null): boolean => {
  const el = target instanceof HTMLElement ? target : null
  if (!el) return false
  return Boolean(
    el.closest('button, a, input, textarea, select, label, [role="button"], .p-checkbox, .p-radiobutton'),
  )
}

const onRowClick = (e: MouseEvent) => {
  if (isInteractiveTarget(e.target)) {
    return
  }
  emit('click', {
    data: props.rowData,
    selectedIdx: props.rowIndex,
  })
}

const onRowContextMenu = (e: MouseEvent) => {
  emit('contextmenu', {
    data: props.rowData,
    selectedIdx: props.rowIndex,
    target: e.target,
    originalEvent: e,
  })
}

const fallbackHtml = computed(() => {
  const content = props.getRowFieldValue('content')
  if (typeof content === 'string') return sanitizePlatformDisplayHtml(content)
  return ''
})

/** row__color с setAttr должен попасть в DOM: CSS чипов иначе не видит выбор. */
const rowMarkColor = computed(() => {
  const row = props.rowData
  if (!row || typeof row !== 'object') return ''
  return readSwingRowString(row as Record<string, unknown>, 'row__color')
})

provide(SwingTilesEmbedKey, true)

const rowActionVisibility = computed(
  () => props.rowActionVisibilityMap?.[props.rowIndex] ?? {},
)

provide(SwingTilesRowScopeKey, {
  tableFieldName: props.tableFieldName,
  rowIndex: props.rowIndex,
  tilesElementId: props.tilesElementId,
  parentPanelId: props.parentPanelId,
  commitRowField: (fieldName, value) => props.commitRowField(fieldName, value),
  getRowFieldValue: (fieldName) => props.getRowFieldValue(fieldName),
  getRowData: () => props.rowData,
  getActionVisibility: (actionName: string) =>
    getTilesRowActionVisibility(props.rowActionVisibilityMap, props.rowIndex, actionName),
  rowActionVisibility,
})

onMounted(async () => {
  await nextTick()
  await nextTick()
  props.afterRowMount?.()
})

watch(
  () => props.panelData,
  (panelData, prev) => {
    if (panelData && !prev) {
      void nextTick(() => {
        props.afterRowMount?.()
      })
    }
  },
)

const rowDataChangeSignature = computed(() =>
  buildTilesRowDataChangeSignature(props.rowData, props.rowIndex),
)

watch(rowDataChangeSignature, (next, prev) => {
  if (!prev || next === prev) return
  void nextTick(() => {
    props.afterRowMount?.()
  })
})
</script>

<style scoped lang="scss">
.swing-tile-row {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;

  > :deep(.m-expansion),
  > :deep(.m-expansion .p-accordion),
  > :deep(.m-expansion .p-accordionpanel),
  > :deep(.m-expansion .p-accordion-panel),
  > :deep(.m-expansion .p-accordioncontent),
  > :deep(.m-expansion .p-accordion-content),
  > :deep(.m-expansion .p-accordioncontent-content),
  > :deep(.m-expansion .expansion-content),
  > :deep(.panel-content-main),
  > :deep(.panel-content),
  > :deep(.elements-row),
  > :deep(.element-container),
  > :deep(.element-container-row),
  > :deep(.element-content-wrapper),
  > :deep(.element-content),
  > :deep(.element-renderer),
  > :deep(.m-html-wrapper),
  > :deep(.m-html-display),
  > :deep(.m-html-display > div) {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    flex: 1 1 auto !important;
    height: 100% !important;
    min-height: 0 !important;
    width: 100% !important;
    box-sizing: border-box;
  }

  > :deep(.elements-row),
  > :deep(.element-container-row) {
    flex-wrap: nowrap !important;
  }

  > :deep(.element-actions-right) {
    flex: 0 0 auto !important;
    height: auto !important;
  }

  :deep(.m-expansion) {
    border-bottom: none;
  }

  :deep(.m-expansion--no-header .p-accordioncontent-content),
  :deep(.m-expansion--no-header .p-accordion-content) {
    padding: 0;
  }

  :deep(.m-html-display) {
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    min-height: 0 !important;
    overflow: visible !important;
  }
}

.swing-tile-row-fallback {
  border: 1px solid #d4d4d4;
  border-radius: 2px;
  padding: 8px;
  background: #f2f2f2;
}
</style>
